import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { MaskLines, Reveal } from "./Reveal";
import { GrainButton } from "./GrainButton";
import { openInquiry } from "../lib/scroll";
import { SERVICES } from "../data/content";

const CARD_THEMES = [
  { bg: "bg-white border border-charcoal/10", text: "text-charcoal", sub: "text-charcoal/60", mono: "text-ember", cta: "text-charcoal hover:text-ember" },
  { bg: "bg-pine", text: "text-cream", sub: "text-cream/65", mono: "text-flame", cta: "text-cream hover:text-flame" },
  { bg: "bg-charcoal", text: "text-cream", sub: "text-cream/65", mono: "text-flame", cta: "text-cream hover:text-flame" },
];

const ServiceCard = ({ service, index }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const scale = useTransform(scrollYProgress, [0, 1], [1, 0.93]);
  const opacity = useTransform(scrollYProgress, [0, 1], [1, 0.4]);
  const theme = CARD_THEMES[index % CARD_THEMES.length];

  return (
    <motion.div
      ref={ref}
      data-testid={`service-panel-${index + 1}`}
      style={{ scale, opacity, top: `${92 + index * 14}px` }}
      className={`sticky mb-8 sm:mb-10 rounded-[2rem] sm:rounded-[2.5rem] overflow-hidden ${theme.bg} will-change-transform`}
    >
      <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-center p-7 sm:p-12 lg:p-16 min-h-[68vh]">
        <div className="lg:col-span-6">
          <div className="flex items-center gap-4 mb-7">
            <span className={`font-mono text-sm ${theme.mono}`}>{service.num}</span>
            <span className={`h-px w-10 ${index === 0 ? "bg-charcoal/20" : "bg-cream/25"}`} />
            <span className={`font-mono text-[11px] sm:text-xs tracking-[0.24em] ${theme.mono}`}>{service.name}</span>
          </div>
          <h3 className={`font-display font-medium tracking-[-0.02em] leading-[1.05] text-3xl sm:text-5xl lg:text-6xl ${theme.text}`}>
            {service.title}
          </h3>
          {service.copy.map((p, i) => (
            <p key={i} className={`mt-5 text-sm sm:text-base leading-relaxed max-w-xl ${theme.sub}`}>{p}</p>
          ))}
          <button
            data-testid={`service-cta-${index + 1}`}
            onClick={() => openInquiry(service.name)}
            className={`group/btn mt-9 inline-flex items-center gap-2 font-mono text-xs tracking-[0.18em] transition-colors duration-300 ${theme.cta}`}
          >
            {service.cta}
            <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover/btn:translate-x-1.5" />
          </button>
        </div>
        <div className="lg:col-span-6">
          <div className="group overflow-hidden rounded-2xl sm:rounded-[1.75rem] aspect-[16/11] lg:aspect-[16/12]">
            <img
              src={service.img}
              alt={service.name}
              loading="lazy"
              className="w-full h-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-[1.06]"
            />
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export const Services = () => {
  return (
    <section id="services" data-testid="services-section" className="px-3 sm:px-5 lg:px-8 py-16 sm:py-32">
      <div className="px-3 sm:px-5 lg:px-8 max-w-[1700px] mx-auto">
        <p className="font-mono text-[11px] sm:text-xs tracking-[0.28em] text-ember mb-5">OUR SERVICES</p>
        <MaskLines
          as="h2"
          lines={[<>What we <span className="font-serif italic font-normal">do.</span></>]}
          className="font-display font-medium tracking-[-0.03em] leading-[1.05] text-4xl sm:text-6xl lg:text-[4.4rem] text-charcoal max-w-5xl"
        />
        <Reveal delay={0.15}>
          <p className="mt-5 text-base sm:text-lg text-charcoal/70 max-w-2xl">From the first idea to the finished landscape.</p>
        </Reveal>
        <div className="mb-14 lg:mb-20" />
      </div>
      <div className="relative max-w-[1700px] mx-auto">
        {SERVICES.map((s, i) => (
          <ServiceCard key={s.num} service={s} index={i} />
        ))}
      </div>
      <Reveal className="mt-14 flex flex-wrap items-center justify-center gap-5 max-w-[1700px] mx-auto" y={20}>
        <GrainButton testid="services-send-inquiry-button" onClick={() => openInquiry()}>
          SEND INQUIRY
          <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
        </GrainButton>
      </Reveal>
    </section>
  );
};
